/**
 * API client for /api/v1/* endpoints.
 *
 * Sends JWT Bearer token from localStorage for authenticated requests.
 * Also sends X-Workspace-Id for workspace scoping.
 */

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || "";
const API_BASE = `${API_ORIGIN}/api/v1`;

interface ApiOptions {
  method?: string;
  body?: unknown;
  params?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(status: number, message: string, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

let currentWorkspaceId: string | null = null;

export function setWorkspaceId(id: string | null) {
  currentWorkspaceId = id;
}

// ─── Auth token management ───────────────────────────────────────────

export function getAuthToken(): string | null {
  return localStorage.getItem("auth_token");
}

export function setAuthToken(token: string | null) {
  if (token) {
    localStorage.setItem("auth_token", token);
  } else {
    localStorage.removeItem("auth_token");
  }
}

export function getUserId(): string | null {
  return localStorage.getItem("user_id");
}

export function setUserId(id: string | null) {
  if (id) {
    localStorage.setItem("user_id", id);
  } else {
    localStorage.removeItem("user_id");
  }
}

export function clearAuth() {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("user_id");
  localStorage.removeItem("rd_workspace_id");
  localStorage.removeItem("rd_project_id");
  localStorage.removeItem("rd_brand_id");
}

export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

// ─── API fetch wrapper ───────────────────────────────────────────────

export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = "GET", body, params, signal } = options;

  let url = `${API_BASE}${path}`;

  if (params) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) search.set(key, String(value));
    }
    const qs = search.toString();
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Add Bearer token
  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (currentWorkspaceId) {
    headers["X-Workspace-Id"] = currentWorkspaceId;
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  // If 401, clear auth and redirect to login
  if (response.status === 401) {
    clearAuth();
    if (window.location.pathname !== "/login" && window.location.pathname !== "/signup") {
      window.location.href = "/login";
    }
  }

  if (!response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      data = { error: response.statusText };
    }
    throw new ApiError(
      response.status,
      (data as { message?: string })?.message || (data as { error?: string })?.error || "Request failed",
      data
    );
  }

  return response.json() as Promise<T>;
}

export function getApiOrigin(): string {
  return API_ORIGIN;
}

// ─── SSE streaming API ───────────────────────────────────────────────

export interface SSEHandlers {
  onTextDelta?: (content: string) => void;
  onToolStart?: (data: { tool: string; label: string; step: number; total_steps: number }) => void;
  onToolProgress?: (data: { step: number; detail: string }) => void;
  onToolComplete?: (data: { step: number; result_summary: string }) => void;
  onKeywords?: (data: { keywords: unknown[]; tags: string[]; stats: unknown }) => void;
  onDone?: (data: { thread_id: string; message_id?: string }) => void;
  onError?: (data: { message: string }) => void;
}

/**
 * POST an SSE streaming request. Reads the response body as text/event-stream
 * and dispatches parsed events to handler callbacks.
 */
export async function apiSSE(
  path: string,
  body: unknown,
  handlers: SSEHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const url = `${API_BASE}${path}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };

  const token = getAuthToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (currentWorkspaceId) headers["X-Workspace-Id"] = currentWorkspaceId;

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal,
  });

  if (response.status === 401) {
    clearAuth();
    if (window.location.pathname !== "/login" && window.location.pathname !== "/signup") {
      window.location.href = "/login";
    }
    return;
  }

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => "Request failed");
    handlers.onError?.({ message: text });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Parse SSE events from buffer
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || ""; // Last part may be incomplete

    for (const part of parts) {
      if (!part.trim() || part.trim() === ":") continue; // heartbeat or empty

      let eventName = "message";
      let dataStr = "";

      for (const line of part.split("\n")) {
        if (line.startsWith("event: ")) {
          eventName = line.slice(7).trim();
        } else if (line.startsWith("data: ")) {
          dataStr += line.slice(6);
        }
      }

      if (!dataStr) continue;

      try {
        const data = JSON.parse(dataStr);
        switch (eventName) {
          case "text_delta":
            handlers.onTextDelta?.(data.content || "");
            break;
          case "tool_start":
            handlers.onToolStart?.(data);
            break;
          case "tool_progress":
            handlers.onToolProgress?.(data);
            break;
          case "tool_complete":
            handlers.onToolComplete?.(data);
            break;
          case "keywords":
            handlers.onKeywords?.(data);
            break;
          case "done":
            handlers.onDone?.(data);
            break;
          case "error":
            handlers.onError?.(data);
            break;
        }
      } catch {
        // Malformed JSON — skip
      }
    }
  }
}

export { ApiError };
